import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BellIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/auth.service';
import NotificationService from '../services/notification.service';
import SettingsService from '../services/settings.service';
import { WorkScheduleManager } from '../components/calendar/WorkScheduleManager';
import toast from '../utils/toast';

const Settings = () => {
  const { isAdmin, isHR, user, updateProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const allowedTabIds = useMemo(() => {
    const base = ['personal', 'security', 'notifications'];
    if (isAdmin || isHR) base.push('policies');
    return base;
  }, [isAdmin, isHR]);

  const pickTabFromSearch = useCallback(
    (sp) => {
      const legacy = { working: 'policies', leave: 'policies', advanced: 'policies' };
      const raw = sp.get('tab');
      const t = legacy[raw] || raw;
      return t && allowedTabIds.includes(t) ? t : 'personal';
    },
    [allowedTabIds]
  );

  const [activeTab, setActiveTab] = useState(() => pickTabFromSearch(searchParams));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    phone: '',
    department: '',
    address: '',
    bio: '',
    profile_picture: ''
  });
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  // Password state (all users)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Notification preferences (all users)
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    browserNotifications: true,
    leaveApproval: true,
    leaveRejection: true,
    claimApproval: true,
    claimRejection: true,
    wfhApproval: true,
    wfhRejection: true,
    announcements: true,
    attendanceReminders: true,
    teamUpdates: true
  });
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    setActiveTab(pickTabFromSearch(searchParams));
  }, [searchParams, pickTabFromSearch]);

  useEffect(() => {
    if (activeTab !== 'personal') return;
    const edit = searchParams.get('edit');
    setProfileEditMode(edit === '1' || edit === 'true');
  }, [activeTab, searchParams]);

  useEffect(() => {
    if (!user) return;
    setProfileData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email_address: user.email || user.email_address || '',
      phone: user.phone || user.phone_no || '',
      department: user.department || user.team?.team_name || '',
      address: user.address || '',
      bio: user.bio || '',
      profile_picture: user.profile_picture || ''
    });
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const result = await updateProfile(profileData);
      if (result?.success) {
        setProfileEditMode(false);
        setSearchParams({ tab: 'personal' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const msg = error.response?.data?.message || 'Failed to update profile';
      toast.error(msg);
    } finally {
      setProfileSaving(false);
    }
  };

  const goToTab = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // HR / Admin — company policies (single tab: schedule + leave + admin-only options)
  const [adminSettings, setAdminSettings] = useState({});

  useEffect(() => {
    if (isAdmin || isHR) {
      fetchSettings();
    }
  }, [isAdmin, isHR]);

  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchNotificationPreferences();
    }
  }, [activeTab]);

  const fetchNotificationPreferences = async () => {
    try {
      setNotifLoading(true);
      const response = await NotificationService.getPreferences();
      const prefs = response?.data || {};
      setNotifications((prev) => ({ ...prev, ...prefs }));
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await SettingsService.getHRSettings();
      const hr = response?.data;
      if (hr) {
        setAdminSettings({
          ...hr,
          financialYearStartMonth: 1,
          leaveAccrualMethod: 'monthly',
          carryForwardPolicy: hr.carryForwardEnabled ? 'partial' : 'none'
        });
      }
    } catch (error) {
      console.error('Error fetching HR settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
        return;
      }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    setSaving(true);
    try {
      await AuthService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to change password';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleNotification = async (key, value) => {
    const next = { ...notifications, [key]: value };
    setNotifications(next);
    try {
      await NotificationService.updatePreferences(next);
      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast.error('Failed to update notifications');
    }
  };

  const handleSaveWorkingHours = async () => {
    const standardWorkingHours = Number(adminSettings.standardWorkingHours);
    if (!Number.isFinite(standardWorkingHours) || standardWorkingHours <= 0 || standardWorkingHours > 24) {
      toast.error('Standard working hours must be greater than 0 and at most 24');
      return;
    }
    try {
      setSaving(true);
      await SettingsService.updateHRSettings({
        standardWorkingHours
      });
      toast.success('Working hours updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error updating working hours:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to update working hours');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLeaveEntitlements = async () => {
    try {
      setSaving(true);
      await SettingsService.updateHRSettings({
        maxPaidLeavePerYear: Number(adminSettings.maxPaidLeavePerYear ?? 21),
        maxSickLeavePerYear: Number(adminSettings.maxSickLeavePerYear ?? 10),
        maxCasualLeavePerYear: Number(adminSettings.maxCasualLeavePerYear ?? 12)
      });
      toast.success('Leave entitlements updated');
      fetchSettings();
    } catch (error) {
      console.error('Error updating leave entitlements:', error);
      toast.error(
        error.message || error.response?.data?.message || 'Failed to update leave entitlements'
      );
    } finally {
      setSaving(false);
    }
  };

  const updateAdmin = (key, value) => {
    setAdminSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAdmin = async () => {
    try {
      setSaving(true);
      const payload = {
        ...adminSettings,
        financialYearStartMonth: 1,
        leaveAccrualMethod: 'monthly',
        carryForwardPolicy: adminSettings.carryForwardEnabled ? 'partial' : 'none'
      };
      await SettingsService.updateHRSettings(payload);
      toast.success('Policies updated successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving admin settings:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to save policies');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAdmin = async () => {
    if (!window.confirm('Are you sure you want to reset all policies to defaults?')) return;
    try {
      setSaving(true);
      const response = await SettingsService.resetHRSettings();
      setAdminSettings(response?.data || {});
      toast.success('Policies reset to defaults');
      fetchSettings();
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to reset policies');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal', icon: UserCircleIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    ...(isAdmin || isHR
      ? [{ id: 'policies', label: 'Company policies', icon: BuildingOfficeIcon }]
      : [])
  ];

    return (
    <div className="space-y-6 pb-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 flex items-center space-x-5">
          <div className="w-16 h-16 bg-white/15 rounded-2xl flex items-center justify-center">
            <Cog6ToothIcon className="w-9 h-9 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1">Settings</h1>
            <p className="text-slate-300">
              Manage your account, preferences and company policies
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-4">
        <div className="lg:col-span-1 lg:sticky lg:top-4 lg:z-10 lg:self-start">
      <Card>
        <Card.Body className="p-0">
              <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                      type="button"
                      onClick={() => goToTab(tab.id)}
                      className={`w-full flex items-center px-4 py-3 text-left transition-colors ${
                    activeTab === tab.id
                          ? 'bg-indigo-50 text-indigo-600 border-r-2 border-indigo-600'
                          : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                      <Icon className="w-5 h-5 mr-3" />
                      {tab.label}
                </button>
              );
            })}
              </nav>
        </Card.Body>
      </Card>
          </div>

        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {activeTab === 'personal' && (
              <motion.div
                key="personal"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Card>
                  <Card.Header>
                    <div className="flex items-center justify-between">
                        <div>
                        <h3 className="text-lg font-semibold">Personal Information</h3>
                        <p className="text-sm text-gray-600">
                          Your personal details and contact information
                        </p>
                        </div>
                      {!profileEditMode && (
            <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setProfileEditMode(true)}
                          icon={PencilIcon}
                        >
                          Edit
            </Button>
                      )}
          </div>
                  </Card.Header>
                  <Card.Body>
                    <form
                      onSubmit={handleProfileUpdate}
                      className="space-y-6 max-w-4xl"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ProfileTextField
                          label="First Name"
                          value={profileData.first_name}
                          disabled={!profileEditMode}
                          onChange={(v) =>
                            setProfileData((p) => ({ ...p, first_name: v }))
                          }
                        />
                        <ProfileTextField
                          label="Last Name"
                          value={profileData.last_name}
                          disabled={!profileEditMode}
                          onChange={(v) =>
                            setProfileData((p) => ({ ...p, last_name: v }))
                          }
                        />
                        </div>

                      <ProfileTextField
                        label="Email Address"
                        icon={EnvelopeIcon}
                        value={profileData.email_address}
                        disabled
                        onChange={() => {}}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ProfileTextField
                          label="Phone Number"
                          icon={PhoneIcon}
                          value={profileData.phone}
                          disabled={!profileEditMode}
                          placeholder="+91 9876543210"
                          onChange={(v) =>
                            setProfileData((p) => ({ ...p, phone: v }))
                          }
                        />
                        <ProfileTextField
                          label="Department"
                          icon={BuildingOfficeIcon}
                          value={profileData.department}
                          disabled={!profileEditMode}
                          onChange={(v) =>
                            setProfileData((p) => ({ ...p, department: v }))
                          }
                        />
                    </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address
                        </label>
                        <div className="relative">
                          <MapPinIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <textarea
                            value={profileData.address}
                            disabled={!profileEditMode}
                            onChange={(e) =>
                              setProfileData((p) => ({
                                ...p,
                                address: e.target.value
                              }))
                            }
                            rows={3}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
                            placeholder="Enter your address"
                          />
                      </div>
                    </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bio
                        </label>
                        <textarea
                          value={profileData.bio}
                          disabled={!profileEditMode}
                          onChange={(e) =>
                            setProfileData((p) => ({ ...p, bio: e.target.value }))
                          }
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-600"
                          placeholder="Tell us about yourself..."
                        />
                    </div>

                      {profileEditMode && (
                        <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                          onClick={() => {
                              setProfileEditMode(false);
                              setSearchParams({ tab: 'personal' });
                              if (user) {
                                setProfileData({
                                  first_name: user.first_name || '',
                                  last_name: user.last_name || '',
                                  email_address:
                                    user.email || user.email_address || '',
                                  phone: user.phone || user.phone_no || '',
                                  department:
                                    user.department || user.team?.team_name || '',
                                  address: user.address || '',
                                  bio: user.bio || '',
                                  profile_picture: user.profile_picture || ''
                                });
                              }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" loading={profileSaving}>
                            Save Changes
                        </Button>
                      </div>
                    )}
                    </form>
                  </Card.Body>
                </Card>
              </motion.div>
            )}

            {activeTab === 'security' && (
              <motion.div
                key="security"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
          <Card>
            <Card.Header>
                    <h3 className="text-lg font-semibold">Change Password</h3>
                    <p className="text-sm text-gray-600">
                      Update your password to keep your account secure
                    </p>
            </Card.Header>
            <Card.Body>
                    <form
                      onSubmit={handlePasswordChange}
                      className="space-y-6 max-w-md"
                    >
                      <PasswordField
                        label="Current Password"
                        value={passwordData.currentPassword}
                        show={showPasswords.current}
                        onChange={(v) =>
                          setPasswordData({ ...passwordData, currentPassword: v })
                        }
                        onToggle={() =>
                          setShowPasswords({
                            ...showPasswords,
                            current: !showPasswords.current
                          })
                        }
                      />
                      <PasswordField
                        label="New Password"
                        value={passwordData.newPassword}
                        show={showPasswords.new}
                        onChange={(v) =>
                          setPasswordData({ ...passwordData, newPassword: v })
                        }
                        onToggle={() =>
                          setShowPasswords({
                            ...showPasswords,
                            new: !showPasswords.new
                          })
                        }
                      />
                      <PasswordField
                        label="Confirm New Password"
                        value={passwordData.confirmPassword}
                        show={showPasswords.confirm}
                        onChange={(v) =>
                          setPasswordData({
                            ...passwordData,
                            confirmPassword: v
                          })
                        }
                        onToggle={() =>
                          setShowPasswords({
                            ...showPasswords,
                            confirm: !showPasswords.confirm
                          })
                        }
                      />
                      <div className="flex justify-end">
                        <Button type="submit" loading={saving}>
                          Change Password
                        </Button>
                </div>
                    </form>
            </Card.Body>
          </Card>
              </motion.div>
            )}

            {activeTab === 'notifications' && (
              <motion.div
                key="notifications"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
          <Card>
            <Card.Header>
                    <h3 className="text-lg font-semibold">Notification Channels</h3>
                    <p className="text-sm text-gray-600">
                      Choose how you want to receive notifications
                    </p>
            </Card.Header>
            <Card.Body>
                    {notifLoading ? (
              <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="h-10 bg-gray-200 animate-pulse rounded-lg"
                          />
                        ))}
                </div>
                    ) : (
              <div className="space-y-3">
                        <ToggleRow
                          label="Email Notifications"
                          description="Receive notifications via email"
                          checked={notifications.emailNotifications}
                          onChange={(v) =>
                            toggleNotification('emailNotifications', v)
                          }
                        />
                        <ToggleRow
                          label="Browser Notifications"
                          description="Real-time browser notifications"
                          checked={notifications.browserNotifications}
                          onChange={(v) =>
                            toggleNotification('browserNotifications', v)
                          }
                        />
                </div>
                    )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
                    <h3 className="text-lg font-semibold">Leave & WFH</h3>
                    <p className="text-sm text-gray-600">
                      Notifications about your leave and WFH requests
                    </p>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                      <ToggleRow
                        label="Leave Approval"
                        checked={notifications.leaveApproval}
                        onChange={(v) => toggleNotification('leaveApproval', v)}
                      />
                      <ToggleRow
                        label="Leave Rejection"
                        checked={notifications.leaveRejection}
                        onChange={(v) => toggleNotification('leaveRejection', v)}
                      />
                      <ToggleRow
                        label="WFH Approval"
                        checked={notifications.wfhApproval}
                        onChange={(v) => toggleNotification('wfhApproval', v)}
                      />
                      <ToggleRow
                        label="WFH Rejection"
                        checked={notifications.wfhRejection}
                        onChange={(v) => toggleNotification('wfhRejection', v)}
                      />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
                    <h3 className="text-lg font-semibold">Claims & Updates</h3>
                    <p className="text-sm text-gray-600">
                      Other notifications you may want to receive
                    </p>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                      <ToggleRow
                        label="Claim Approval"
                        checked={notifications.claimApproval}
                        onChange={(v) => toggleNotification('claimApproval', v)}
                      />
                      <ToggleRow
                        label="Claim Rejection"
                        checked={notifications.claimRejection}
                        onChange={(v) => toggleNotification('claimRejection', v)}
                      />
                      <ToggleRow
                        label="Announcements"
                        checked={notifications.announcements}
                        onChange={(v) => toggleNotification('announcements', v)}
                      />
                      <ToggleRow
                        label="Attendance Reminders"
                        checked={notifications.attendanceReminders}
                        onChange={(v) => toggleNotification('attendanceReminders', v)}
                      />
                      <ToggleRow
                        label="Team Updates"
                        checked={notifications.teamUpdates}
                        onChange={(v) => toggleNotification('teamUpdates', v)}
                      />
              </div>
            </Card.Body>
          </Card>
              </motion.div>
            )}

            {activeTab === 'policies' && (isAdmin || isHR) && (
              <motion.div
                key="policies"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-4 sm:px-5">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Company policies
                  </h3>
                  <p className="text-sm text-slate-600">
                    Standard workday, leave limits, and (for admins) accrual and
                    carry-forward rules — in one place so nothing is duplicated.
                  </p>
                </div>

                <WorkScheduleManager />

                {isAdmin && (
                  <div className="flex flex-col gap-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Admin-only options
                      </p>
                      <p className="text-sm text-gray-600">
                        Save accrual, carry-forward, week schedule, workday, and
                        encashment settings below.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3 sm:justify-end">
                      <Button
                        variant="outline"
                        onClick={handleResetAdmin}
                        loading={saving}
                        icon={ArrowPathIcon}
                      >
                        Reset defaults
                      </Button>
                      <Button onClick={handleSaveAdmin} loading={saving}>
                        Save admin policies
                      </Button>
                    </div>
                  </div>
                )}

            <Card className="overflow-hidden border-sky-200">
              <Card.Header className="border-sky-100 bg-sky-50/80">
                    <h3 className="text-lg font-semibold">Standard workday</h3>
                    <p className="text-sm text-gray-600">
                      Configure the number of hours required for a standard full
                      working day.
                    </p>
              </Card.Header>
              <Card.Body className="bg-gradient-to-br from-white to-sky-50/60 py-6">
                    {loading ? (
                <div className="space-y-4">
                        <div className="h-10 bg-gray-200 animate-pulse rounded-lg" />
                        <div className="h-10 bg-gray-200 animate-pulse rounded-lg" />
                      </div>
                    ) : (
                      <div className="grid gap-5 rounded-xl border border-sky-100 bg-white/80 p-4 shadow-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-end sm:p-5">
                        <Input
                          label="Standard working hours per day"
                          type="number"
                          step="0.25"
                          value={adminSettings.standardWorkingHours ?? 8.5}
                          onChange={(e) =>
                            updateAdmin('standardWorkingHours', parseFloat(e.target.value))
                          }
                          min="0.25"
                          max="24"
                          helperText="Used as the full-day attendance threshold"
                          containerClassName="max-w-xl"
                          className="border-sky-200 bg-white focus:border-sky-500 focus:ring-sky-500/20"
                        />
                        <div className="flex justify-end">
                          <Button variant="softBlue" size="lg" className="min-w-40 shadow-sm" onClick={handleSaveWorkingHours} loading={saving}>
                            Save workday
                          </Button>
                        </div>
                      </div>
                    )}
              </Card.Body>
            </Card>

            <Card className="overflow-hidden border-emerald-200">
              <Card.Header className="border-emerald-100 bg-emerald-50/80">
                    <h3 className="text-lg font-semibold">Leave entitlements</h3>
                    <p className="text-sm text-gray-600">
                      Yearly limits for paid, sick, and casual leave
                    </p>
              </Card.Header>
              <Card.Body className="bg-gradient-to-br from-white to-emerald-50/60 py-6">
                    {loading ? (
                <div className="space-y-4">
                        <div className="h-10 bg-gray-200 animate-pulse rounded-lg" />
                        <div className="h-10 bg-gray-200 animate-pulse rounded-lg" />
                  </div>
                    ) : (
                      <div className="space-y-5">
                        <div className="grid gap-4 md:grid-cols-3">
                <Input
                          label="Max paid leave per year"
                  type="number"
                          value={adminSettings.maxPaidLeavePerYear ?? 21}
                          onChange={(e) =>
                            updateAdmin(
                              'maxPaidLeavePerYear',
                              parseInt(e.target.value, 10)
                            )
                          }
                  min="0"
                  max="365"
                  containerClassName="rounded-xl border border-emerald-100 bg-white/90 p-4 shadow-sm"
                  className="border-emerald-200 bg-white focus:border-emerald-500 focus:ring-emerald-500/20"
                />
                <Input
                          label="Max sick leave per year"
                  type="number"
                          value={adminSettings.maxSickLeavePerYear ?? 12}
                          onChange={(e) =>
                            updateAdmin(
                              'maxSickLeavePerYear',
                              parseInt(e.target.value, 10)
                            )
                          }
                  min="0"
                  max="365"
                  containerClassName="rounded-xl border border-emerald-100 bg-white/90 p-4 shadow-sm"
                  className="border-emerald-200 bg-white focus:border-emerald-500 focus:ring-emerald-500/20"
                />
                <Input
                          label="Max casual leave per year"
                  type="number"
                          value={adminSettings.maxCasualLeavePerYear ?? 12}
                          onChange={(e) =>
                            updateAdmin(
                              'maxCasualLeavePerYear',
                              parseInt(e.target.value, 10)
                            )
                          }
                  min="0"
                  max="365"
                  containerClassName="rounded-xl border border-emerald-100 bg-white/90 p-4 shadow-sm"
                  className="border-emerald-200 bg-white focus:border-emerald-500 focus:ring-emerald-500/20"
                />
                        </div>
                        <div className="flex justify-end border-t border-emerald-100 pt-4">
                          <Button
                            variant="softGreen"
                            size="lg"
                            className="min-w-40 shadow-sm"
                            onClick={handleSaveLeaveEntitlements}
                            loading={saving}
                          >
                            Save leave limits
                          </Button>
                        </div>
                      </div>
                    )}
              </Card.Body>
            </Card>

                {isAdmin && (
                <>
            <Card className="overflow-hidden border-violet-200">
              <Card.Header className="border-violet-100 bg-violet-50/80">
                    <h4 className="font-semibold">Accrual Configuration</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Define leave accrual logic and leave-year boundaries
                    </p>
              </Card.Header>
              <Card.Body className="space-y-4 bg-gradient-to-br from-white to-violet-50/50 py-5">
                <Select
                  label="Accrual Method"
                      value="monthly"
                      disabled
                  options={[
                    { value: 'monthly', label: 'Monthly Accrual' }
                  ]}
                  helperText="Paid leave is earned monthly from the joining month"
                />
                <Input
                  label="Monthly Accrual Rate"
                  type="number"
                  step="0.01"
                      value={adminSettings.monthlyAccrualRate ?? 1}
                      onChange={(e) =>
                        updateAdmin(
                          'monthlyAccrualRate',
                          parseFloat(e.target.value)
                        )
                      }
                  min="0"
                  max="10"
                  helperText="1 day per month provides 12 days over 12 months"
                />
                <Select
                  label="Financial Year Start"
                      value={1}
                      disabled
                  options={[
                    { value: 1, label: 'January' }
                  ]}
                  helperText="Fixed calendar year: January through December"
                />
              </Card.Body>
            </Card>

            <Card className="overflow-hidden border-amber-200">
              <Card.Header className="border-amber-100 bg-amber-50/80">
                    <h4 className="font-semibold">Carry Forward Policy</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Control unused leave carry-forward behavior
                    </p>
              </Card.Header>
              <Card.Body className="space-y-4 bg-gradient-to-br from-white to-amber-50/50 py-5">
                    <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                        checked={adminSettings.carryForwardEnabled || false}
                        onChange={(e) =>
                          updateAdmin('carryForwardEnabled', e.target.checked)
                        }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                      <span className="text-sm font-medium text-gray-700">
                    Enable Leave Carry Forward
                      </span>
                  </label>

                    {adminSettings.carryForwardEnabled && (
                  <>
                      <Input
                        label="Carry Forward Percentage"
                        type="number"
                            value={adminSettings.carryForwardPercent ?? 50}
                            onChange={(e) =>
                              updateAdmin(
                                'carryForwardPercent',
                                parseInt(e.target.value, 10)
                              )
                            }
                        min="0"
                        max="100"
                        helperText="Percentage of the final adjusted balance eligible for carry-forward"
                      />

                    <Input
                      label="Maximum Carry Forward Days"
                      type="number"
                          value={adminSettings.carryForwardMaxDays ?? 12}
                          onChange={(e) =>
                            updateAdmin(
                              'carryForwardMaxDays',
                              parseInt(e.target.value, 10)
                            )
                          }
                      min="0"
                      max="365"
                      helperText="Final result is rounded down and capped at this value"
                    />
                    <p className="text-sm text-gray-600">
                      At year end, the percentage is applied to the employee&apos;s
                      final balance after that year&apos;s manual adjustments.
                    </p>
                  </>
                )}
              </Card.Body>
            </Card>

            <Card className="overflow-hidden border-cyan-200">
              <Card.Header className="border-cyan-100 bg-cyan-50/80">
                    <h4 className="font-semibold">Week schedule & encashment</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Working days per week, grace period, and leave encashment.
                      Standard hours per day come from the standard workday above.
                    </p>
              </Card.Header>
              <Card.Body className="space-y-4 bg-gradient-to-br from-white to-cyan-50/50 py-5">
                <Input
                  label="Working Days Per Week"
                  type="number"
                      value={adminSettings.workingDaysPerWeek ?? 5}
                      onChange={(e) =>
                        updateAdmin(
                          'workingDaysPerWeek',
                          parseInt(e.target.value, 10)
                        )
                      }
                  min="1"
                  max="7"
                />
                <Input
                  label="Late Arrival Grace Period (Minutes)"
                  type="number"
                      value={adminSettings.lateArrivalGracePeriod ?? 15}
                      onChange={(e) =>
                        updateAdmin(
                          'lateArrivalGracePeriod',
                          parseInt(e.target.value, 10)
                        )
                      }
                  min="0"
                  max="120"
                />

                    <div className="pt-2 border-t border-gray-100 space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                          checked={adminSettings.leaveEncashmentEnabled || false}
                          onChange={(e) =>
                            updateAdmin(
                              'leaveEncashmentEnabled',
                              e.target.checked
                            )
                          }
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                        <span className="text-sm font-medium text-gray-700">
                      Enable Leave Encashment
                        </span>
                    </label>

                      {adminSettings.leaveEncashmentEnabled && (
                    <Input
                      label="Maximum Encashment Days"
                      type="number"
                          value={adminSettings.maxEncashmentDays ?? 10}
                          onChange={(e) =>
                            updateAdmin(
                              'maxEncashmentDays',
                              parseInt(e.target.value, 10)
                            )
                          }
                      min="0"
                      max="30"
                    />
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 shrink-0" />
                    <div className="ml-3 text-sm text-yellow-700">
                          Leave encashment may have tax and finance implications.
                          Validate policy before enabling.
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
                </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
              </div>
              </div>
              </div>
  );
};

const ProfileTextField = ({
  label,
  value,
  onChange,
  icon: Icon,
  disabled,
  placeholder
}) => (
              <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      )}
                <input
                  type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${
          Icon ? 'pl-10' : 'pl-3'
        } pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-600`}
                />
              </div>
  </div>
);

const PasswordField = ({ label, value, onChange, show, onToggle }) => (
              <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <div className="relative">
      <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
      <button
                  type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 transform -translate-y-1/2"
      >
        {show ? (
          <EyeSlashIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <EyeIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>
                </div>
                </div>
);

const ToggleRow = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div>
      <div className="text-sm font-medium text-gray-900">{label}</div>
      {description && <div className="text-xs text-gray-600">{description}</div>}
              </div>
    <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
    </div>
  );

export default Settings;
